import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Block from '@mui/icons-material/Block';
import { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useContext } from 'react';

import Menu from '~components/Menu/Menu';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import {
  AVAILABLE_CHARGES_ISSUE_TYPES,
  CHARGES_ISSUE_TYPES,
  ChargesException,
  ChargesIssueType,
  ChargesViewTab,
  deriveIssueFilters,
} from '../approveChargesTabs';

const issueTypeLabelKeys = {
  [ChargesIssueType.AI_FLAGGED]: 'approvals.approve_charges.issues.types.ai_flagged',
  [ChargesIssueType.DUPLICATE_TICKETS]:
    'approvals.approve_charges.issues.types.duplicate_tickets',
  [ChargesIssueType.FLAGGED_TICKETS]:
    'approvals.approve_charges.issues.types.flagged_tickets',
  [ChargesIssueType.MISSING_QUANTITY]:
    'approvals.approve_charges.issues.types.missing_quantity',
  [ChargesIssueType.MISSING_TICKET]:
    'approvals.approve_charges.issues.types.missing_ticket',
} satisfies Record<ChargesIssueType, string>;

export default function ChargesExceptionChips() {
  const {
    exception,
    isAiApprovedOnly,
    issueTypes,
    setIssueTypes,
    toggleAiApprovedOnly,
    toggleException,
    viewTab,
  } = useContext(ApproveChargesPageContext);
  const { applyFilters } = useContext(FilesQueryContext);

  const isIssuesOn = exception === ChargesException.ISSUES;
  const isRejectedOn = exception === ChargesException.REJECTED;

  const handleToggleIssues = useCallback(() => {
    applyFilters(deriveIssueFilters(!isIssuesOn, issueTypes));
    toggleException(ChargesException.ISSUES);
  }, [applyFilters, isIssuesOn, issueTypes, toggleException]);

  const handleToggleRejected = useCallback(() => {
    applyFilters(deriveIssueFilters(false, issueTypes));
    toggleException(ChargesException.REJECTED);
  }, [applyFilters, issueTypes, toggleException]);

  const handleToggleIssueType = useCallback(
    (issueType: ChargesIssueType) => {
      const next = issueTypes.includes(issueType)
        ? issueTypes.filter((type) => type !== issueType)
        : [...issueTypes, issueType];

      setIssueTypes(next);

      if (isIssuesOn) {
        applyFilters(deriveIssueFilters(true, next));
      }
    },
    [applyFilters, isIssuesOn, issueTypes, setIssueTypes],
  );

  const issuesLabel =
    issueTypes.length === 1
      ? t(issueTypeLabelKeys[issueTypes[0]])
      : t('approvals.approve_charges.issues.loads_with_issues');

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
      {viewTab === ChargesViewTab.ALL && !isRejectedOn && (
        <Tooltip title={t('approvals.approve_charges.ai_approved.tooltip')}>
          <ButtonBase
            aria-pressed={isAiApprovedOnly}
            data-test-id="ai-approved-chip"
            onClick={toggleAiApprovedOnly}
            sx={(theme) => ({
              ...chipPartSx,
              ...getChipSx(theme, isAiApprovedOnly),
              px: 1.25,
            })}
          >
            {t('approvals.approve_charges.ai_approved.label')}
          </ButtonBase>
        </Tooltip>
      )}

      <Tooltip title={t('approvals.approve_charges.issues.tooltip')}>
        <Box
          data-test-id="loads-with-issues-chip"
          sx={(theme) => ({ ...getChipSx(theme, isIssuesOn), display: 'flex' })}
        >
          <ButtonBase
            aria-pressed={isIssuesOn}
            onClick={handleToggleIssues}
            sx={{ ...chipPartSx, pl: 1.25, pr: 0.75 }}
          >
            {issuesLabel}
          </ButtonBase>
          <Menu
            menuTrigger={
              <ButtonBase
                aria-label={t('approvals.approve_charges.issue_types')}
                sx={{ ...chipPartSx, borderLeft: '1px solid', px: 0.25 }}
              >
                <ArrowDropDown fontSize="small" />
              </ButtonBase>
            }
          >
            {CHARGES_ISSUE_TYPES.map((issueType) => {
              const isAvailable = AVAILABLE_CHARGES_ISSUE_TYPES.includes(issueType);
              const isLastTickedType =
                issueTypes.length === 1 && issueTypes.includes(issueType);

              return (
                <MenuItem key={issueType} dense disabled={!isAvailable}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAvailable && issueTypes.includes(issueType)}
                        disabled={!isAvailable || isLastTickedType}
                        onChange={() => handleToggleIssueType(issueType)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="subtitle2">
                        {t(issueTypeLabelKeys[issueType])}
                        {!isAvailable &&
                          ` (${t('approvals.approve_charges.issues.coming_soon')})`}
                      </Typography>
                    }
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      </Tooltip>

      <ButtonBase
        aria-pressed={isRejectedOn}
        data-test-id="rejected-mode-chip"
        onClick={handleToggleRejected}
        sx={(theme) => ({
          ...chipPartSx,
          ...getChipSx(theme, isRejectedOn),
          gap: 0.5,
          px: 1.25,
        })}
      >
        <Block sx={{ fontSize: '16px' }} />
        {t('approvals.approve_charges.rejected')}
      </ButtonBase>
    </Box>
  );
}

const SMALL_CHIP_HEIGHT_IN_PX = 24;

const chipPartSx = {
  alignSelf: 'stretch',
  color: 'inherit',
  font: 'inherit',
  fontWeight: 500,
  whiteSpace: 'nowrap',
} as const;

const getChipSx = (theme: Theme, isOn: boolean) => ({
  alignItems: 'center',
  bgcolor: isOn ? theme.brandV2.colors.treadGray1 : 'transparent',
  border: `solid 1px ${isOn ? theme.brandV2.colors.treadGray1 : theme.brandV2.colors.treadGray7}`,
  borderRadius: '16px',
  color: isOn ? theme.semantic.textInverse : theme.brandV2.colors.treadBlack,
  fontSize: '13px',
  height: SMALL_CHIP_HEIGHT_IN_PX,
  overflow: 'hidden',
});
